import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLoginMutation } from "../../redux/api/AuthApi/authApi";
import { setLogin } from "../../redux/slices/authSlice";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  InputGroup,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Eye, EyeOff, Lock, Mail, ChevronRight } from "lucide-react";
import { brandLogo, brandName } from "../../Constants/content";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await login(formData).unwrap();
      dispatch(setLogin(response));
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err?.data?.message || "Invalid credentials. Please try again.");
    }
  };

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        backgroundColor: "#d6e6f7ff",
        padding: "20px",
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col xl={10}>
            <Card
              className="border-0 overflow-hidden"
              style={{ borderRadius: "24px" }}
            >
              <Row className="g-0">
                {/* Left Side: Branding & Info */}
                <Col
                  lg={6}
                  className="d-none d-lg-block"
                  style={{
                    background:
                      "linear-gradient(135deg, #0C95CC 0%, #05516e 100%)",
                    position: "relative",
                    padding: "60px",
                  }}
                >
                  <div className="h-100 d-flex flex-column justify-content-between text-white">
                    <div>
                      <img
                        src={brandLogo}
                        alt="Ideal Logo"
                        style={{
                          height: "60px",
                          filter: "brightness(0) invert(1)",
                          marginBottom: "40px",
                        }}
                      />
                      <h1
                        className="fw-bold mb-3"
                        style={{ fontSize: "2.5rem", lineHeight: "1.2" }}
                      >
                        Empowering Your <br />
                        <span style={{ color: "var(--secondary-color)" }}>
                          Digital Evolution
                        </span>
                      </h1>
                      <p className="fs-5 opacity-75">
                        Seamlessly manage your corporate services with our
                        advanced administrative dashboard.
                      </p>
                    </div>

                    <div>
                      <div className="d-flex align-items-center mb-4">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "rgba(255,255,255,0.1)",
                          }}
                        >
                          <ChevronRight size={20} />
                        </div>
                        <span>Secure Enterprise access protocol</span>
                      </div>
                      <p className="small opacity-50 mb-0">
                        &copy; {new Date().getFullYear()} {brandName}. <br />
                        Excellence in Corporate Solutions.
                      </p>
                    </div>
                  </div>

                  {/* Decorative Elements */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-50px",
                      right: "-50px",
                      width: "200px",
                      height: "200px",
                      backgroundColor: "rgba(188, 210, 47, 0.1)",
                      borderRadius: "50%",
                      filter: "blur(40px)",
                    }}
                  />
                </Col>

                {/* Right Side: Login Form */}
                <Col lg={6} className="bg-white p-4 p-md-5">
                  <div className="d-lg-none mb-4 text-center">
                    <img
                      src={brandLogo}
                      alt="Logo"
                      style={{ height: "40px" }}
                    />
                  </div>

                  <div className="mb-5">
                    <h2 className="fw-bold text-dark mb-2">Sign In</h2>
                    <p className="text-muted">
                      Enter your credentials to access the admin panel
                    </p>
                  </div>

                  {error && (
                    <Alert
                      variant="danger"
                      className="border-0 shadow-sm rounded-3 py-2 mb-4"
                    >
                      {error}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label className="small text-uppercase fw-bold text-muted tracking-wider">
                        Work Email
                      </Form.Label>
                      <InputGroup
                        className="border rounded-3 overflow-hidden shadow-sm"
                        style={{ transition: "all 0.3s ease" }}
                      >
                        <InputGroup.Text className="bg-white border-0">
                          <Mail size={18} className="text-muted" />
                        </InputGroup.Text>
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder="name@ideal.com"
                          className="border-0 py-2 ps-1"
                          style={{ boxShadow: "none" }}
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-5">
                      <Form.Label className="small text-uppercase fw-bold text-muted tracking-wider d-flex justify-content-between">
                        Password
                      </Form.Label>
                      <InputGroup className="border rounded-3 overflow-hidden shadow-sm">
                        <InputGroup.Text className="bg-white border-0">
                          <Lock size={18} className="text-muted" />
                        </InputGroup.Text>
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="••••••••"
                          className="border-0 py-2 ps-1"
                          style={{ boxShadow: "none" }}
                          value={formData.password}
                          onChange={handleChange}
                          required
                        />
                        <InputGroup.Text
                          className="bg-white border-0 cursor-pointer"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ cursor: "pointer" }}
                        >
                          {showPassword ? (
                            <EyeOff size={18} className="text-muted" />
                          ) : (
                            <Eye size={18} className="text-muted" />
                          )}
                        </InputGroup.Text>
                      </InputGroup>
                    </Form.Group>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-100 py-3 fw-bold text-uppercase border-0 shadow-sm"
                      style={{
                        backgroundColor: "var(--primary-color)",
                        borderRadius: "12px",
                        letterSpacing: "1px",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {isLoading ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Verifying Access...
                        </div>
                      ) : (
                        "Authenticate"
                      )}
                    </Button>
                  </Form>

                  <div className="mt-5 text-center px-4">
                    <p className="small text-muted mb-0">
                      Need technical assistance or lost access?
                      <a
                        href="mailto:support@ideal.com"
                        className="ms-1 fw-bold text-decoration-none"
                        style={{ color: "var(--primary-color)" }}
                      >
                        Contact Support
                      </a>
                    </p>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;
